/**
 * Montants en FCFA (XOF) : toujours des entiers, jamais de flottants.
 * Le FCFA n'a pas de subdivision utilisée, le montant minimal est 1 FCFA.
 */

export type Fcfa = number;

export class InvalidAmountError extends Error {
  constructor(value: unknown) {
    super(`Montant FCFA invalide : ${String(value)} (entier positif attendu)`);
    this.name = 'InvalidAmountError';
  }
}

export function isValidFcfa(value: unknown): value is Fcfa {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

export function assertFcfa(value: unknown): Fcfa {
  if (!isValidFcfa(value)) throw new InvalidAmountError(value);
  return value;
}

const NBSP = ' ';

/** Sépare les milliers par une espace : 50000 → "50 000". */
export function groupThousands(value: number, separator = ' '): string {
  const sign = value < 0 ? '-' : '';
  const digits = Math.abs(Math.trunc(value)).toString();
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/**
 * Formate un montant : 50000 → "50 000 FCFA".
 * `nonBreaking` utilise des espaces insécables pour éviter les retours à la ligne dans l'UI.
 */
export function formatFcfa(value: Fcfa, options: { nonBreaking?: boolean; withCurrency?: boolean } = {}): string {
  const { nonBreaking = false, withCurrency = true } = options;
  const sep = nonBreaking ? NBSP : ' ';
  const amount = groupThousands(value, sep);
  return withCurrency ? `${amount}${sep}FCFA` : amount;
}

/** Lit une saisie utilisateur ("50 000", "50.000", "50000 FCFA") et renvoie un entier ou null. */
export function parseFcfa(input: string | number | null | undefined): Fcfa | null {
  if (input === null || input === undefined) return null;
  if (typeof input === 'number') return isValidFcfa(input) ? input : null;
  const cleaned = input.replace(/fcfa|xof|cfa/gi, '').replace(/[\s  .,_]/g, '');
  if (cleaned === '') return null;
  if (!/^\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isSafeInteger(value) ? value : null;
}

/** Applique un taux exprimé en pour mille, arrondi à l'entier le plus proche. */
export function applyPerMille(amount: Fcfa, perMille: number): Fcfa {
  return Math.round((amount * perMille) / 1000);
}

/** Pourcentage (0–100) d'avancement, borné, arrondi à l'unité. */
export function progressPercent(collected: Fcfa, goal: Fcfa): number {
  if (goal <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((collected / goal) * 100)));
}
